import {
	Building,
	CircleCheckBig,
	CircleEllipsis,
	ContactRound,
	FolderPlus,
	Folders,
	Inbox,
	Mail,
	MailQuestion,
	MapPin,
	MessageSquareDot,
	Phone,
	SquarePen,
	Tags,
} from 'lucide-react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	ActionSheet,
	BottomSheetTextButton,
	Chip,
	ChipRow,
	MobileHeader,
	MobileHeaderButton,
	SegmentedControl,
} from '@/components/mobile';
import { cn } from '@/lib/utils';
import {
	getUserDepartmentIds,
	isAssignedToUser,
	isUnassigned,
} from '@/utils/webchat/conversationAssignment';
import { channelOptions } from '../ChannelFilter';

/** The name of the inbox view a list shows, for the phone's large title. */
export const getInboxViewTitle = (
	assignedTypeSelected,
	{ users = [], departments = [], folders = [], businessGroup = [] } = {},
) => {
	if (!assignedTypeSelected || assignedTypeSelected === 'ALL') return 'Inbox';
	if (assignedTypeSelected === 'UNASSIGNED') return 'Unassigned';
	if (assignedTypeSelected === 'MY_MESSAGES') return 'My Messages';

	const [type, ...rest] = assignedTypeSelected.split('_');
	const id = rest.join('_');

	if (type === 'FOLDER') return folders.find((f) => f._id === id)?.title || 'Folder';
	if (type === 'TAG') return id || 'Tag';
	if (type === 'USER') return users.find((u) => u._id === id)?.name || 'Team member';
	if (type === 'DEPARTMENT') return departments.find((d) => d._id === id)?.name || 'Department';
	if (type === 'LOCATION') return businessGroup.find((b) => b._id === id)?.name || 'Location';

	return 'Inbox';
};

/**
 * The phone inbox's navigation bar: Select on the left, and the views menu
 * and compose on the right, over a large title naming the current view. The
 * large title scrolls away with the list (the bar stays) and the bar then
 * shows the title, like iOS. In Select mode it becomes Select all / Done with
 * the selected count as the title.
 */
export function InboxMobileHeader({
	title,
	titleCollapsed,
	largeTitleRef,
	selectMode,
	selectedCount,
	allSelected,
	onEnterSelect,
	onToggleSelectAll,
	onDone,
	onOpenViews,
	onCompose,
}) {
	const shownTitle = selectMode
		? selectedCount
			? `${selectedCount} selected`
			: 'Select conversations'
		: title;

	return (
		<>
			<MobileHeader
				className={cn(
					'transition-[border-color,background-color] duration-150 supports-[backdrop-filter]:bg-background/85',
					// Short screens (landscape) skip the large title: the bar shows it.
					!titleCollapsed &&
						'border-transparent bg-background supports-[backdrop-filter]:bg-background [@media(max-height:500px)]:border-border/60',
				)}
				title={
					// The large title below is the heading; this copy is visual only.
					<span
						aria-hidden="true"
						className={cn(
							'transition-opacity duration-150',
							titleCollapsed ? 'opacity-100' : 'opacity-0 [@media(max-height:500px)]:opacity-100',
						)}
					>
						{shownTitle}
					</span>
				}
				left={
					selectMode ? (
						<BottomSheetTextButton onClick={onToggleSelectAll}>
							{allSelected ? 'Deselect all' : 'Select all'}
						</BottomSheetTextButton>
					) : (
						<BottomSheetTextButton onClick={onEnterSelect}>Select</BottomSheetTextButton>
					)
				}
				right={
					selectMode ? (
						<BottomSheetTextButton strong onClick={onDone}>
							Done
						</BottomSheetTextButton>
					) : (
						<>
							<MobileHeaderButton aria-label="Inbox views" onClick={onOpenViews}>
								<CircleEllipsis />
							</MobileHeaderButton>
							<MobileHeaderButton aria-label="New conversation" onClick={onCompose}>
								<SquarePen />
							</MobileHeaderButton>
						</>
					)
				}
			/>
			<div
				ref={largeTitleRef}
				className="shrink-0 px-4 pb-2 md:hidden [@media(max-height:500px)]:sr-only"
			>
				<h1 className="truncate text-[28px] font-bold leading-tight tracking-tight">{shownTitle}</h1>
			</div>
		</>
	);
}

/**
 * Open / Closed and the quick filters under the phone search field. The
 * chips scroll sideways inside their row.
 */
export function InboxMobileFilters({
	openConversationsSelected,
	onOpenChange,
	showOnlyUnread,
	onUnreadChange,
	channelFilter,
	onChannelChange,
}) {
	const channels = channelOptions.filter((option) => option.value !== 'All');

	return (
		<div className="flex shrink-0 items-center gap-2 pb-2 pl-4 md:hidden">
			<SegmentedControl
				aria-label="Conversation status"
				value={openConversationsSelected ? 'OPEN' : 'CLOSED'}
				onValueChange={(value) => onOpenChange(value === 'OPEN')}
				options={[
					{ value: 'OPEN', label: 'Open' },
					{ value: 'CLOSED', label: 'Closed' },
				]}
				className="w-[9.5rem] shrink-0"
			/>
			<ChipRow aria-label="Filters" className="min-w-0 flex-1 py-1.5 pl-0.5 pr-4">
				<Chip
					active={showOnlyUnread}
					icon={MessageSquareDot}
					onClick={() => onUnreadChange(!showOnlyUnread)}
				>
					Unread
				</Chip>
				{channels.map((option) => {
					const active = channelFilter === option.value;
					return (
						<Chip
							key={option.value}
							active={active}
							onClick={() => onChannelChange(active ? 'All' : option.value)}
						>
							{option.label}
						</Chip>
					);
				})}
			</ChipRow>
		</div>
	);
}

const countOpen = (list) => list.filter((c) => c.open).length;

/**
 * The phone's list of inbox views (the same ones the desktop sidebar shows):
 * Inbox, Unassigned, My Messages, then folders, tags, people, departments and
 * locations as drill-in pages.
 */
export function InboxViewsSheet({
	open,
	onOpenChange,
	onCreateFolder,
	onCall,
	callDisabled,
	onSelect,
}) {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const auth = useSelector((state) => state.auth);
	const customerConversations = useSelector((state) => state.webchat.customerConversations) || [];
	const folders = useSelector((state) => state.folders) || [];
	const tags = useSelector((state) => state.tags) || [];
	const users = useSelector((state) => state.users) || [];
	const departments = useSelector((state) => state.departments) || [];
	const businessGroup = useSelector((state) => state.businessGroup) || [];

	const user = auth.user || {};
	const isEnterpriseEnabled = !!user.isEnterpriseEnabled;
	const currentBusiness = user._temporaryBusiness;

	const counts = useMemo(() => {
		if (!open) return {};
		const departmentIds = getUserDepartmentIds(departments, user._id);
		return {
			inbox: countOpen(customerConversations),
			unassigned: countOpen(customerConversations.filter(isUnassigned)),
			mine: countOpen(
				customerConversations.filter((c) => isAssignedToUser(c, user._id, departmentIds)),
			),
		};
	}, [open, customerConversations, departments, user._id]);

	const isCurrent = (url) => pathname === url || pathname.startsWith(`${url}/`);
	const go = (url) => () => {
		if (!isCurrent(url)) navigate(url);
	};
	const viewItem = (id, label, url, extra = {}) => ({
		id,
		label,
		checked: isCurrent(url),
		onSelect: go(url),
		...extra,
	});

	// Enterprise accounts see people and departments grouped by location.
	const groupByBusiness = (items, belongsTo) =>
		isEnterpriseEnabled
			? businessGroup
					.map((business) => ({
						id: business._id,
						title: business.name,
						items: items.filter((item) => belongsTo(item, business._id)),
					}))
					.filter((section) => section.items.length)
			: [{ id: 'all', items: items.filter((item) => belongsTo(item, currentBusiness)) }];

	const peopleSections = groupByBusiness(
		users.filter((u) => u.active !== false),
		(u, businessId) => (u._businesses || []).includes(businessId),
	).map((section) => ({
		...section,
		items: section.items.map((u) =>
			viewItem(u._id, u.name || u.username, `/users/${u._id}`),
		),
	}));

	const departmentSections = groupByBusiness(
		departments.filter((d) => d.isActive),
		(d, businessId) => d._business === businessId,
	).map((section) => ({
		...section,
		items: section.items.map((d) => viewItem(d._id, d.name, `/departments/${d._id}`)),
	}));
	const hasDepartments = departmentSections.some((section) => section.items.length);

	const sections = [
		{
			id: 'views',
			items: [
				viewItem('inbox', 'Inbox', '/inbox', { icon: Inbox, trailing: counts.inbox }),
				viewItem('unassigned', 'Unassigned', '/unassigned', {
					icon: MailQuestion,
					trailing: counts.unassigned,
				}),
				viewItem('mine', 'My Messages', '/my-messages', { icon: Mail, trailing: counts.mine }),
			],
		},
		{
			id: 'organize',
			items: [
				{
					id: 'folders',
					label: 'Folders',
					icon: Folders,
					page: 'folders',
					trailing: folders.length || undefined,
				},
				{ id: 'tags', label: 'Tags', icon: Tags, page: 'tags', trailing: tags.length || undefined },
				{ id: 'people', label: 'Team members', icon: ContactRound, page: 'people' },
				hasDepartments && {
					id: 'departments',
					label: 'Departments',
					icon: Building,
					page: 'departments',
				},
				isEnterpriseEnabled && {
					id: 'locations',
					label: 'Locations',
					icon: MapPin,
					page: 'locations',
				},
			].filter(Boolean),
		},
		{
			id: 'actions',
			items: [
				onCall && { id: 'call', label: 'Call a number', icon: Phone, disabled: callDisabled, onSelect: onCall },
				{ id: 'select', label: 'Select conversations', icon: CircleCheckBig, onSelect },
			].filter(Boolean),
		},
	];

	const pages = [
		{
			id: 'folders',
			title: 'Folders',
			searchable: folders.length > 8,
			searchPlaceholder: 'Search folders',
			sections: [
				folders.length > 0 && {
					id: 'folders',
					items: folders.map((folder) =>
						viewItem(folder._id, folder.title || 'Untitled folder', `/folders/${folder._id}`),
					),
				},
				{
					id: 'create',
					items: [{ id: 'create', label: 'New folder', icon: FolderPlus, onSelect: onCreateFolder }],
				},
			].filter(Boolean),
		},
		{
			id: 'tags',
			title: 'Tags',
			searchable: tags.length > 8,
			searchPlaceholder: 'Search tags',
			emptyText: 'No matching tags',
			sections: tags.length
				? [{ id: 'tags', items: tags.map((tag) => viewItem(tag, tag, `/tags/${tag}`)) }]
				: [{ id: 'none', items: [{ id: 'none', label: 'No tags yet', disabled: true }] }],
		},
		{
			id: 'people',
			title: 'Team members',
			searchable: true,
			searchPlaceholder: 'Search people',
			sections: peopleSections,
		},
		{
			id: 'departments',
			title: 'Departments',
			searchable: departments.length > 8,
			searchPlaceholder: 'Search departments',
			sections: departmentSections,
		},
		{
			id: 'locations',
			title: 'Locations',
			searchable: businessGroup.length > 8,
			searchPlaceholder: 'Search locations',
			sections: [
				{
					id: 'locations',
					items: businessGroup.map((business) =>
						viewItem(business._id, business.name, `/locations/${business._id}`),
					),
				},
			],
		},
	];

	return (
		<ActionSheet
			open={open}
			onOpenChange={onOpenChange}
			title="Inbox views"
			sections={sections}
			pages={pages}
		/>
	);
}

/** Placeholder rows while the phone list loads, shaped like the real rows. */
export function MobileRowSkeletons({ count = 8 }) {
	return (
		<div aria-hidden="true" className="flex flex-col">
			{Array.from({ length: count }, (_, i) => (
				<div key={i} className="flex min-h-[72px] items-start gap-3 pl-4">
					<div className="my-3 size-12 shrink-0 animate-pulse rounded-full bg-muted" />
					<div className={cn('flex flex-1 flex-col gap-2 border-b border-border/60 py-3.5 pr-4')}>
						<div className="flex justify-between gap-4">
							<div className="h-4 w-2/5 animate-pulse rounded bg-muted" />
							<div className="h-3 w-10 animate-pulse rounded bg-muted" />
						</div>
						<div className="h-3.5 w-full animate-pulse rounded bg-muted" />
						<div className="h-3.5 w-3/5 animate-pulse rounded bg-muted" />
					</div>
				</div>
			))}
		</div>
	);
}
